import { useMemo, useState } from 'react';
import type { IconType } from 'react-icons';
import { FaUserGroup } from 'react-icons/fa6';
import { IoClose } from 'react-icons/io5';

import { Avatar } from '@/components/Avatar/Avatar';
import { ButtonSmall } from '@/components/Button';
import type { MappedPersonForSelection } from '@/store/persons/person.dto';

import type { LogCrewMember } from '../hooks/useLogCrew';
import type { CrewRosterEntry } from '../types';

import s from './CrewRosterSection.module.scss';
import { CrewStatusPicker } from './CrewStatusPicker';
import { SectionHeader } from './SectionHeader';
import { UserPickerModal } from './UserPickerModal';

interface CrewRosterSectionProps {
  crewRoster: CrewRosterEntry[];
  allUsers: MappedPersonForSelection[];
  addedIds: Set<string>;
  /**
   * The Crew Management record behind each row, by person id — read for the
   * member's current role. The status shown is the entry's own.
   */
  crewById: Map<string, LogCrewMember>;
  /** True once the captain has signed: the entry is a record, not a form. */
  disabled: boolean;
  onAdd: (user: CrewRosterEntry) => void;
  onUpdate: (userId: string, patch: Partial<CrewRosterEntry>) => void;
  onRemove: (userId: string) => void;
  icon?: IconType;
}

export function CrewRosterSection({
  crewRoster,
  allUsers,
  addedIds,
  crewById,
  disabled,
  onAdd,
  onUpdate,
  onRemove,
  icon: Icon = FaUserGroup,
}: CrewRosterSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);

  /**
   * One pass over the roster for both layouts, so the desktop table and the
   * mobile cards cannot disagree about someone's role.
   *
   * The crew record is missing for a name this entry kept after they left the
   * vessel; the role then falls back to whatever the log saved, which is all a
   * past member's row can say.
   */
  const rows = useMemo(
    () =>
      crewRoster.map((entry) => ({
        entry,
        roleName: crewById.get(entry.userId)?.role || entry.roleName,
      })),
    [crewRoster, crewById],
  );

  return (
    <div className={s.section}>
      <SectionHeader icon={Icon} title="Crew Roster" />

      {crewRoster.length === 0 ? (
        <p className={s.empty}>Please add crew members from the vessel’s crew.</p>
      ) : (
        <>
          <div className={s.tableWrapper}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th colSpan={2}>Name</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ entry, roleName }) => (
                  <tr key={entry.userId}>
                    <td className={s.avatarCell}>
                      <Avatar
                        firstName={entry.firstName}
                        lastName={entry.lastName}
                        src={entry.avatar}
                        size={26}
                      />
                    </td>
                    <td className={s.nameCell}>
                      <div className={s.name}>
                        {entry.firstName} {entry.lastName}
                      </div>
                      <div className={s.role}>{roleName}</div>
                    </td>
                    <td className={s.statusCell}>
                      <CrewStatusPicker
                        status={entry.status}
                        personName={`${entry.firstName} ${entry.lastName}`}
                        editable={!disabled}
                        onSet={(status) => onUpdate(entry.userId, { status })}
                      />
                    </td>
                    <td className={s.actionCell}>
                      <button
                        className={s.removeBtn}
                        onClick={() => onRemove(entry.userId)}
                        title="Remove from Log Entry"
                      >
                        <IoClose />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={s.mobileList}>
            {rows.map(({ entry, roleName }) => (
              <div key={entry.userId} className={s.mobileCard}>
                <div className={s.mobileCardHeader}>
                  <div className={s.mobileCardIdentity}>
                    <Avatar
                      firstName={entry.firstName}
                      lastName={entry.lastName}
                      src={entry.avatar}
                      size={32}
                    />
                    <div>
                      <div className={s.name}>
                        {entry.firstName} {entry.lastName}
                      </div>
                      <div className={s.role}>{roleName}</div>
                      <div className={s.mobileStatus}>
                        <CrewStatusPicker
                          status={entry.status}
                          personName={`${entry.firstName} ${entry.lastName}`}
                          editable={!disabled}
                          onSet={(status) => onUpdate(entry.userId, { status })}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    className={s.removeBtn}
                    onClick={() => onRemove(entry.userId)}
                    title="Remove from Log Entry"
                  >
                    <IoClose />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <ButtonSmall onClick={() => setModalOpen(true)}>+ Add Crew Member</ButtonSmall>

      <UserPickerModal
        open={modalOpen}
        users={allUsers}
        disabledIds={addedIds}
        // Someone added back mid-entry starts where the crew record has them,
        // the same as the names the entry opened with.
        onSelect={(user) => {
          const member = crewById.get(user.userId);
          onAdd(member ? { ...user, roleName: member.role, status: member.presence } : user);
        }}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
